/**
 * Auth Service
 *
 * All authentication API calls using cookie-based session management.
 * Cookies are sent automatically via httpClient (credentials: 'include').
 */

import { httpClient } from '../api/http-client.js';

// Types matching backend responses
export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  isAdmin: boolean;
  isAdminVerified: boolean;
  emailVerified: boolean;
  createdAt: string;
}

export interface UserProfile extends User {
  primaryEmail?: string;
  phoneNumber?: string;
  organizationId?: string;
  profilePictureUrl?: string;
  bio?: string;
  metadata?: Record<string, any>;
}

export interface Role {
  id: string;
  name: string;
  description?: string;
  isDefault: boolean;
  createdAt: string;
}

export interface Permission {
  id: string;
  name: string;
  description?: string;
  resource?: string;
  action?: string;
}

export interface SessionInfo {
  sessionId: string;
  userId: string;
  expiresAt: string;
}

// Request/Response types
export interface LoginRequest {
  email: string;
  password: string;
}

export interface ResetPasswordRequest {
  email: string;
  redirectUrl?: string;
}

export interface UpdatePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface UpdateProfileRequest {
  firstName?: string;
  lastName?: string;
  /** The server's PUT /auth/profile reads `phone` (work phone); it ignores any other field but the names. */
  phone?: string | null;
  bio?: string;
  profilePictureUrl?: string;
  metadata?: Record<string, any>;
}

/**
 * Auth Service - All authentication operations
 */
export const authService = {
  /**
   * Login with email and password
   * Sets session cookie automatically
   */
  async login(data: LoginRequest): Promise<{ user: User }> {
    return httpClient.post<{ user: User }>('/auth/login', data);
  },

  /**
   * Logout and clear session
   * Removes session cookie
   */
  async logout(): Promise<{ success: boolean }> {
    return httpClient.post<{ success: boolean }>('/auth/logout', {});
  },

  /**
   * Refresh Supabase tokens (server-side only)
   * Cookie stays the same, server rotates tokens internally
   */
  async refresh(): Promise<{ success: boolean }> {
    return httpClient.post<{ success: boolean }>('/auth/refresh');
  },

  /**
   * Get current session info
   */
  async getSession(): Promise<SessionInfo> {
    return httpClient.get<SessionInfo>('/auth/session');
  },

  /**
   * Get current user
   */
  async getCurrentUser(): Promise<{ user: User }> {
    return httpClient.get<{ user: User }>('/auth/me');
  },

  /**
   * Get user permissions.
   *
   * Backend returns `{ success, data: { permissions: string[], role } }`
   * — the response envelope wraps an array of permission *name strings* sourced
   * from `permissions.perm_name`. The store + every consumer (hasPermission,
   * useFeatureAccess) expects `Permission[]` with a `.name` property, so we
   * unwrap the envelope and rehydrate each string into a `{ id, name }` object.
   *
   * Without this transform, the store ends up with `permissions: undefined`,
   * `useFeatureAccess` falls through to its bootstrap tier check, and every
   * Feature Access matrix toggle becomes a silent no-op for non-admin users.
   */
  async getPermissions(): Promise<{ permissions: Permission[] }> {
    const res = await httpClient.get<{
      success?: boolean;
      data?: { permissions?: string[]; role?: string };
      // Tolerate a flat response in case a future backend change drops the
      // envelope — we still produce a coherent shape downstream.
      permissions?: string[] | Permission[];
    }>('/auth/permissions');

    const raw = res.data?.permissions ?? res.permissions ?? [];
    const permissions: Permission[] = (raw as Array<string | Permission>).map(
      (entry) =>
        typeof entry === 'string'
          ? ({ id: entry, name: entry } as Permission)
          : entry
    );
    return { permissions };
  },

  /**
   * Get full user profile
   */
  async getProfile(): Promise<{ profile: UserProfile; role: Role | null }> {
    return httpClient.get<{ profile: UserProfile; role: Role | null }>('/auth/profile');
  },

  /**
   * Update user profile
   */
  async updateProfile(data: UpdateProfileRequest): Promise<{ profile: UserProfile }> {
    return httpClient.put<{ profile: UserProfile }>('/auth/profile', data);
  },

  /**
   * Update password
   */
  async updatePassword(data: UpdatePasswordRequest): Promise<{ success: boolean }> {
    return httpClient.put<{ success: boolean }>('/auth/update-password', data);
  },

  /**
   * Request password reset email
   */
  async resetPassword(data: ResetPasswordRequest): Promise<{ success: boolean; message: string }> {
    return httpClient.post<{ success: boolean; message: string }>('/auth/reset-password', data);
  },

  /**
   * Resend email verification
   */
  async resendVerification(): Promise<{ success: boolean; message: string }> {
    return httpClient.post<{ success: boolean; message: string }>('/auth/resend-verification');
  },
};
